// @ts-nocheck
import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, RotateCcw, User, Bot, Trophy, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/api-client';
import { buildTrainerPrompt } from './coldCallTrainerPrompt';

// ── Scenario config ───────────────────────────────────────────────────────────
// Labels/titles resolved from the catalog at render time (see the `usePersonas`
// etc. lists built inside the component); only the values stay fixed here.
const PERSONA_VALUES = ['ceo', 'cto', 'drh', 'it'];
const DIFFICULTY_VALUES = ['easy', 'medium', 'hard'];
const OBJECTIVE_VALUES = ['rdv', 'qualification', 'demo'];

// ── Fallback response pools ───────────────────────────────────────────────────

const RESPONSE_POOLS = {
  objections: [
    "Nous avons déjà un fournisseur, merci.",
    "Pas le budget cette année, désolé.",
    "Envoyez-moi un email, je verrai ça plus tard.",
    "Je n'ai vraiment pas le temps là.",
    "On n'est pas sur le marché en ce moment.",
  ],
  interest: [
    "Ça m'intéresse, pouvez-vous m'en dire plus ?",
    "Quels sont vos tarifs ?",
    "Comment vous comparez-vous à vos concurrents ?",
    "Combien de temps prend l'intégration ?",
  ],
  neutral: [
    "Hmm, je vois...",
    "D'accord, continuez.",
    "Qu'est-ce que vous voulez dire exactement ?",
    "Je n'avais pas pensé à ça sous cet angle.",
  ],
};

function pickRandom(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function getLocalResponse(difficulty, msgCount) {
  // Increase objection rate with difficulty and early in the call
  const objRate = difficulty === 'hard' ? 0.6 : difficulty === 'medium' ? 0.4 : 0.2;
  const interestRate = difficulty === 'hard' ? 0.2 : difficulty === 'medium' ? 0.35 : 0.5;

  const rand = Math.random();
  if (rand < objRate) return pickRandom(RESPONSE_POOLS.objections);
  if (rand < objRate + interestRate) return pickRandom(RESPONSE_POOLS.interest);
  return pickRandom(RESPONSE_POOLS.neutral);
}

// ── Score calculation ─────────────────────────────────────────────────────────

function computeScore(messages) {
  const sdrMessages = messages.filter(m => m.role === 'sdr');
  const prospectMessages = messages.filter(m => m.role === 'prospect');
  const objections = prospectMessages.filter(m =>
    RESPONSE_POOLS.objections.some(o => m.text.includes(o.slice(0, 20)))
  ).length;
  const interests = prospectMessages.filter(m =>
    RESPONSE_POOLS.interest.some(o => m.text.includes(o.slice(0, 20)))
  ).length;

  const base = Math.min(sdrMessages.length * 8, 40);
  const objBonus = objections * 5;
  const interestBonus = interests * 10;
  return Math.min(100, base + objBonus + interestBonus);
}

// ── Typing indicator ──────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="flex items-end gap-2 justify-start">
      <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
        <Bot className="w-3.5 h-3.5 text-muted-foreground" />
      </div>
      <div className="bg-card border border-border rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
        <div className="flex items-center gap-1">
          <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
          <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
          <span className="w-1.5 h-1.5 bg-muted-foreground/60 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ColdCallTrainer() {
  const { t } = useTranslation(['dialer']);
  const PERSONAS = PERSONA_VALUES.map((value) => ({
    value,
    label: t(`dialer:coldCallTrainer.personas.${value}.label`),
    title: t(`dialer:coldCallTrainer.personas.${value}.title`),
  }));
  const DIFFICULTIES = DIFFICULTY_VALUES.map((value) => ({
    value, label: t(`dialer:coldCallTrainer.difficulties.${value}`),
  }));
  const OBJECTIVES = OBJECTIVE_VALUES.map((value) => ({
    value, label: t(`dialer:coldCallTrainer.objectives.${value}`),
  }));
  const [persona, setPersona] = useState('ceo');
  const [difficulty, setDifficulty] = useState('medium');
  const [objective, setObjective] = useState('rdv');
  const [sessionStarted, setSessionStarted] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [score, setScore] = useState(null);
  const messagesEndRef = useRef(null);

  const personaLabel = PERSONAS.find(p => p.value === persona)?.title || t('dialer:coldCallTrainer.prospectFallback');
  const objectiveLabel = OBJECTIVES.find(o => o.value === objective)?.label || '';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isThinking]);

  const startSession = () => {
    const greeting = `[Sonnerie] Allô ?`;
    setMessages([{ id: 1, role: 'prospect', text: greeting, ts: Date.now() }]);
    setSessionStarted(true);
    setSessionEnded(false);
    setScore(null);
    setInput('');
  };

  const resetSession = () => {
    setMessages([]);
    setSessionStarted(false);
    setSessionEnded(false);
    setScore(null);
    setInput('');
  };

  const endSession = useCallback(() => {
    const finalScore = computeScore(messages);
    setScore(finalScore);
    setSessionEnded(true);
  }, [messages]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || isThinking || sessionEnded) return;

    const sdrMsg = { id: Date.now(), role: 'sdr', text, ts: Date.now() };
    setMessages(prev => [...prev, sdrMsg]);
    setInput('');
    setIsThinking(true);

    // Simulate thinking delay
    const delay = 900 + Math.random() * 700;
    await new Promise(r => setTimeout(r, delay));

    let prospectResponse = '';
    try {
      // AUDIT-ADM-12: this used to call analyze-call without callId nor
      // callNotes (always 400), so every answer was a canned local one. The
      // role play now goes to the model through /ai/invoke.
      const result = await api.integrations.Core.InvokeLLM({
        feature: 'cold_call_trainer',
        prompt: buildTrainerPrompt({ persona, difficulty, objective, history: [...messages, sdrMsg] }),
      });
      prospectResponse = typeof result === 'string' ? result.trim() : '';
    } catch (_err) {
      // fallback
    }

    if (!prospectResponse) {
      prospectResponse = getLocalResponse(difficulty, messages.length);
    }

    const prospectMsg = { id: Date.now() + 1, role: 'prospect', text: prospectResponse, ts: Date.now() };
    setMessages(prev => [...prev, prospectMsg]);
    setIsThinking(false);

    // Auto-end after 20 exchanges (10 rounds)
    if (messages.length + 2 >= 20) {
      setTimeout(() => endSession(), 400);
    }
  }, [input, isThinking, sessionEnded, messages, persona, difficulty, objective, endSession]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sdrCount = messages.filter(m => m.role === 'sdr').length;
  const objectionCount = messages.filter(m =>
    m.role === 'prospect' && RESPONSE_POOLS.objections.some(o => m.text.includes(o.slice(0, 15)))
  ).length;

  // ── Pre-session config ────────────────────────────────────────────────────

  if (!sessionStarted) {
    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-6 max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-sm">
            <Bot className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-foreground">{t('dialer:coldCallTrainer.title')}</h3>
            <p className="text-xs text-muted-foreground">{t('dialer:coldCallTrainer.subtitle')}</p>
          </div>
        </div>

        <div className="space-y-4 mb-6">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">
              {t('dialer:coldCallTrainer.personaLabel')}
            </label>
            <Select value={persona} onValueChange={setPersona}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PERSONAS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">
                {t('dialer:coldCallTrainer.difficultyLabel')}
              </label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTIES.map(d => (
                    <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5 block">
                {t('dialer:coldCallTrainer.objectiveLabel')}
              </label>
              <Select value={objective} onValueChange={setObjective}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {OBJECTIVES.map(o => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-100 rounded-xl p-3 mb-4">
          <p className="text-xs text-orange-700 font-medium">
            {t('dialer:coldCallTrainer.simulationText', {
              persona: PERSONAS.find(p => p.value === persona)?.label,
              difficulty: DIFFICULTIES.find(d => d.value === difficulty)?.label.toLowerCase(),
              objective: objectiveLabel,
            })}
          </p>
        </div>

        <Button
          onClick={startSession}
          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold gap-2"
        >
          <Send className="w-4 h-4" />
          {t('dialer:coldCallTrainer.startSession')}
        </Button>
      </div>
    );
  }

  // ── Post-session summary ──────────────────────────────────────────────────

  if (sessionEnded && score !== null) {
    const scoreColor = score >= 70 ? 'text-green-600' : score >= 40 ? 'text-orange-500' : 'text-red-500';
    const scoreLabel = score >= 70 ? t('dialer:coldCallTrainer.scoreLabels.excellent') : score >= 40 ? t('dialer:coldCallTrainer.scoreLabels.good') : t('dialer:coldCallTrainer.scoreLabels.toImprove');

    return (
      <div className="bg-card rounded-2xl border border-border shadow-sm p-6 max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-md">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-xl font-bold text-foreground mb-1">{t('dialer:coldCallTrainer.sessionEnded')}</h3>
          <p className="text-sm text-muted-foreground">{personaLabel}, {objectiveLabel}</p>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-5">
          <div className="surface-1 rounded-xl p-3 text-center border border-border/50">
            <p className="text-2xl font-bold text-foreground">{sdrCount}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('dialer:coldCallTrainer.messagesSent')}</p>
          </div>
          <div className="surface-1 rounded-xl p-3 text-center border border-border/50">
            <p className="text-2xl font-bold text-foreground">{objectionCount}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('dialer:coldCallTrainer.objectionsHandled')}</p>
          </div>
          <div className={`surface-1 rounded-xl p-3 text-center border border-border/50`}>
            <p className={`text-2xl font-bold ${scoreColor}`}>{score}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{t('dialer:coldCallTrainer.scoreOutOf100')}</p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 p-3 mb-4">
          <p className="text-sm font-semibold text-indigo-800 text-center">{scoreLabel}</p>
          {score < 70 && (
            <p className="text-xs text-indigo-600 text-center mt-1">
              {t('dialer:coldCallTrainer.advice')}
            </p>
          )}
        </div>

        <Button
          onClick={resetSession}
          className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold gap-2"
        >
          <RotateCcw className="w-4 h-4" />
          {t('dialer:coldCallTrainer.newSession')}
        </Button>
      </div>
    );
  }

  // ── Active chat ───────────────────────────────────────────────────────────

  return (
    <div className="bg-card rounded-2xl border border-border shadow-sm flex flex-col max-w-2xl mx-auto" style={{ height: '520px' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/50 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center">
            <Bot className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{personaLabel}</p>
            <p className="text-xs text-muted-foreground/60">{objectiveLabel} · {DIFFICULTIES.find(d => d.value === difficulty)?.label}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">
            {sdrCount} msg
          </Badge>
          <button
            onClick={endSession}
            className="text-xs px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg border border-red-200 transition-colors font-medium"
          >
            {t('dialer:coldCallTrainer.endSession')}
          </button>
          <button
            onClick={resetSession}
            className="p-1.5 hover:bg-accent rounded-lg transition-colors"
          >
            <RotateCcw className="w-3.5 h-3.5 text-muted-foreground/60" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.map(msg => (
          <div
            key={msg.id}
            className={`flex items-end gap-2 ${msg.role === 'sdr' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role === 'prospect' && (
              <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                <Bot className="w-3.5 h-3.5 text-muted-foreground" />
              </div>
            )}
            <div
              className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                msg.role === 'sdr'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-card border border-border text-foreground rounded-bl-sm'
              }`}
            >
              {msg.text}
            </div>
            {msg.role === 'sdr' && (
              <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <User className="w-3.5 h-3.5 text-blue-600" />
              </div>
            )}
          </div>
        ))}

        {isThinking && <TypingIndicator />}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-border/50 flex-shrink-0">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('dialer:coldCallTrainer.replyPlaceholder')}
            className="flex-1 text-sm"
            disabled={isThinking || sessionEnded}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isThinking || sessionEnded}
            className="w-9 h-9 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center transition-colors"
          >
            {isThinking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-muted-foreground/60 mt-1.5 text-center">
          {t('dialer:coldCallTrainer.enterToSend')} {Math.max(0, 20 - messages.length)} {t('dialer:coldCallTrainer.exchangesLeft')}
        </p>
      </div>
    </div>
  );
}
